import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './Documents.css';

const CATEGORY_ICONS = {
  Policies:   'fa-file-alt',
  Procedures: 'fa-list-ol',
  Templates:  'fa-file-invoice',
  Compliance: 'fa-clipboard-check',
};

const CATEGORY_COLORS = {
  Policies:   '#1e3a5f',
  Procedures: '#0e7490',
  Templates:  '#6d28d9',
  Compliance: '#065f46',
};

const WORKFLOW_COLORS = {
  DRAFT:     { bg: '#fef3c7', text: '#92400e' },
  SUBMITTED: { bg: '#dbeafe', text: '#1e40af' },
  ACCEPTED:  { bg: '#d1fae5', text: '#065f46' },
  IN_FORCE:  { bg: '#ede9fe', text: '#5b21b6' },
};

const CATEGORIES = ['Policies', 'Procedures', 'Compliance', 'Templates'];

function CategoryIcon({ category }) {
  return (
    <i
      className={`fas ${CATEGORY_ICONS[category] || 'fa-file'}`}
      style={{ color: CATEGORY_COLORS[category] || '#64748b' }}
    />
  );
}

function WorkflowBadge({ status }) {
  const colors = WORKFLOW_COLORS[status] || { bg: '#f1f5f9', text: '#475569' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, backgroundColor: colors.bg, color: colors.text,
    }}>
      {status || 'DRAFT'}
    </span>
  );
}

function Documents() {
  const { t } = useTranslation();
  const [tab, setTab]                         = useState('templates'); // 'templates' | 'my-docs'
  const [documents, setDocuments]             = useState([]);
  const [orgDocs, setOrgDocs]                 = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [orgDocsLoading, setOrgDocsLoading]   = useState(false);
  const [error, setError]                     = useState(null);
  const [searchQuery, setSearchQuery]         = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [view, setView]                       = useState('grid');
  const [uploading, setUploading]             = useState(false);
  const [showUpload, setShowUpload]           = useState(false);
  const [uploadForm, setUploadForm]           = useState({ title: '', category: '', description: '' });
  const [uploadFile, setUploadFile]           = useState(null);
  const [inheriting, setInheriting]           = useState(null); // templateId being inherited
  const [previewDoc, setPreviewDoc]           = useState(null); // doc object being previewed
  const [previewContent, setPreviewContent]   = useState('');
  const [previewLoading, setPreviewLoading]   = useState(false);
  const [orgProfile, setOrgProfile]           = useState(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const role = localStorage.getItem('role');

  useEffect(() => {
    Promise.allSettled([
      axiosInstance.get('/documents'),
      axiosInstance.get('/org-profile'),
    ]).then(([docsResult, profileResult]) => {
      if (docsResult.status === 'fulfilled') {
        setDocuments(Array.isArray(docsResult.value.data) ? docsResult.value.data : []);
      } else {
        setError(docsResult.reason?.message);
      }
      if (profileResult.status === 'fulfilled') {
        setOrgProfile(profileResult.value.data);
      }
    }).finally(() => setLoading(false));
  }, []);

  const loadOrgDocs = () => {
    setOrgDocsLoading(true);
    axiosInstance.get('/org-documents')
      .then(res => setOrgDocs(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOrgDocs([]))
      .finally(() => setOrgDocsLoading(false));
  };

  useEffect(() => {
    if (tab === 'my-docs') loadOrgDocs();
  }, [tab]);

  const displayed = documents.filter(doc =>
    (selectedCategory === 'all' || doc.category === selectedCategory) &&
    (!searchQuery ||
      (doc.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.category || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.description || '').toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleView = (doc) => {
    navigate('/dashboard/document-details', { state: { document: doc } });
  };

  const handleInherit = async (templateId, e) => {
    e.stopPropagation();
    setInheriting(templateId);
    try {
      await axiosInstance.post(`/org-documents/inherit/${templateId}`);
      setTab('my-docs');
    } catch {
      alert(t('Documents.inheritFailed'));
    } finally {
      setInheriting(null);
    }
  };

  const handleOpenOrgDoc = (doc) => {
    const base = role === 'ROLE_PLATFORM_ADMIN' ? '/adminHome' : '/dashboard';
    navigate(base + '/org-documents/' + doc.id);
  };

  const handleDeleteOrgDoc = async (doc, e) => {
    if (e) e.stopPropagation();
    const c = await Swal.fire({
      title: t('Documents.deleteDocTitle', 'Delete this document?'),
      html: t('Documents.deleteDocHtml', `"${doc.title}" will be permanently removed.`),
      icon: 'warning', showCancelButton: true, confirmButtonText: t('Documents.deleteConfirm', 'Delete'),
      confirmButtonColor: '#ef4444',
    });
    if (!c.isConfirmed) return;
    try {
      await axiosInstance.delete(`/org-documents/${doc.id}`);
      loadOrgDocs();
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: t('Documents.docDeleted', 'Document deleted'), showConfirmButton: false, timer: 2000 });
    } catch (err) {
      Swal.fire(t('Documents.errorTitle', 'Error'), err?.response?.data?.message || t('Documents.deleteFailed', 'Could not delete the document'), 'error');
    }
  };

  const handleUnpublishOrgDoc = async (doc, e) => {
    if (e) e.stopPropagation();
    try {
      await axiosInstance.post(`/org-documents/${doc.id}/unpublish`);
      loadOrgDocs();
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: t('Documents.docUnpublished', 'Document unpublished'), showConfirmButton: false, timer: 2000 });
    } catch (err) {
      Swal.fire(t('Documents.errorTitle', 'Error'), err?.response?.data?.message || t('Documents.unpublishFailed', 'Could not unpublish the document'), 'error');
    }
  };

  const handlePreview = async (doc, e) => {
    e.stopPropagation();
    setPreviewDoc(doc);
    setPreviewContent('');
    setPreviewLoading(true);
    try {
      const res = await axiosInstance.get(`/documents/${doc.id}/content`);
      setPreviewContent(res.data.content || '');
    } catch {
      setPreviewContent(t('Documents.previewLoadFailed'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.title.trim()) { alert(t('Documents.titleRequired')); return; }
    if (!uploadFile) { alert(t('Documents.fileRequired')); return; }
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('title', uploadForm.title);
    fd.append('category', uploadForm.category);
    fd.append('description', uploadForm.description);
    try {
      setUploading(true);
      // Org-scoped upload -> appears under "My Documents" (never the shared template library).
      await axiosInstance.post('/org-documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowUpload(false);
      setUploadForm({ title: '', category: '', description: '' });
      setUploadFile(null);
      setTab('my-docs');
      loadOrgDocs();
    } catch (e) {
      alert(e?.response?.data?.message || e?.response?.data || t('Documents.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };


  const categoryCounts = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = documents.filter(d => d.category === cat).length;
    return acc;
  }, {});

  if (loading) return <div className="doc-loading"><div className="doc-spinner" /></div>;
  if (error)   return <div className="doc-error">{t('Documents.error', { error })}</div>;

  return (
    <div className="doc-page">
      {/* Stat bar */}
      <div className="doc-stat-bar">
        <div className="doc-stat">
          <span className="doc-stat-val">{documents.length}</span>
          <span className="doc-stat-label">{t('Documents.totalTemplates')}</span>
        </div>
        {CATEGORIES.map(cat => (
          <div key={cat} className="doc-stat">
            <span className="doc-stat-val">{categoryCounts[cat] || 0}</span>
            <span className="doc-stat-label">{cat}</span>
          </div>
        ))}
        <div className="doc-stat">
          <span className="doc-stat-val">{orgDocs.length}</span>
          <span className="doc-stat-label">{t('Documents.myDocuments')}</span>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="doc-tabs">
        <button
          className={`doc-tab-btn ${tab === 'templates' ? 'doc-tab-active' : ''}`}
          onClick={() => setTab('templates')}
        >
          <i className="fas fa-layer-group" /> {t('Documents.templateLibrary')}
        </button>
        <button
          className={`doc-tab-btn ${tab === 'my-docs' ? 'doc-tab-active' : ''}`}
          onClick={() => setTab('my-docs')}
        >
          <i className="fas fa-folder-open" /> {t('Documents.myDocuments')}
          {orgDocs.length > 0 && <span className="doc-tab-count">{orgDocs.length}</span>}
        </button>
      </div>

      {/* ── Template Library tab ── */}
      {tab === 'templates' && (
        <div className="doc-card">
          <div className="doc-toolbar">
            <div className="doc-search-wrap">
              <i className="fas fa-search doc-search-icon" />
              <input
                type="text"
                className="doc-search"
                placeholder={t('Documents.searchPlaceholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="doc-filter-wrap">
              <i className="fas fa-filter doc-filter-icon" />
              <select
                className="doc-select"
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
              >
                <option value="all">{t('Documents.allCategories')}</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="doc-view-toggle">
              <button
                className={`doc-view-btn ${view === 'grid' ? 'doc-view-active' : ''}`}
                onClick={() => setView('grid')}
                title={t('Documents.gridView')}
              >
                <i className="fas fa-th-large" />
              </button>
              <button
                className={`doc-view-btn ${view === 'list' ? 'doc-view-active' : ''}`}
                onClick={() => setView('list')}
                title={t('Documents.listView')}
              >
                <i className="fas fa-list" />
              </button>
            </div>

            {role === 'ROLE_ADMIN' && (
              <button className="doc-upload-btn" onClick={() => setShowUpload(true)}>
                <i className="fas fa-upload" /> {t('Documents.upload')}
              </button>
            )}
          </div>

          {displayed.length === 0 ? (
            <div className="doc-empty">
              {searchQuery || selectedCategory !== 'all'
                ? t('Documents.noDocumentsMatch')
                : t('Documents.noDocumentsAvailable')}
            </div>
          ) : view === 'grid' ? (
            <div className="doc-grid">
              {displayed.map(doc => (
                <div key={doc.id} className="doc-item">
                  <div className="doc-item-icon" onClick={() => handleView(doc)}>
                    <CategoryIcon category={doc.category} />
                  </div>
                  <div className="doc-item-body" onClick={() => handleView(doc)}>
                    <div className="doc-item-title">{doc.title}</div>
                    <div className="doc-item-meta">
                      <span className="doc-cat-badge">{doc.category || t('Documents.uncategorized')}</span>
                      {doc.documentId && <span className="doc-id-badge">{doc.documentId}</span>}
                    </div>
                    {doc.description && <p className="doc-item-desc">{doc.description}</p>}
                  </div>
                  <div className="doc-item-actions">
                    <button
                      className="doc-preview-btn"
                      title={t('Documents.previewTitle')}
                      onClick={e => handlePreview(doc, e)}
                    >
                      <i className="fas fa-eye" /> {t('Documents.preview')}
                    </button>
                    <button
                      className="doc-inherit-btn"
                      title={t('Documents.inheritTitle')}
                      disabled={inheriting === doc.id}
                      onClick={e => handleInherit(doc.id, e)}
                    >
                      {inheriting === doc.id
                        ? <i className="fas fa-spinner fa-spin" />
                        : <><i className="fas fa-copy" /> {t('Documents.inherit')}</>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="doc-table-wrap">
              <table className="doc-table">
                <thead>
                  <tr>
                    <th>{t('Documents.colTitle')}</th>
                    <th>{t('Documents.colCategory')}</th>
                    <th>{t('Documents.colId')}</th>
                    <th>{t('Documents.colDescription')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map(doc => (
                    <tr key={doc.id} className="doc-row">
                      <td className="doc-row-title" onClick={() => handleView(doc)}>
                        <CategoryIcon category={doc.category} />
                        <span>{doc.title}</span>
                      </td>
                      <td><span className="doc-cat-badge">{doc.category || '—'}</span></td>
                      <td className="doc-row-id">{doc.documentId || '—'}</td>
                      <td className="doc-row-desc">{doc.description || '—'}</td>
                      <td className="doc-row-action" style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="doc-inherit-btn"
                          disabled={inheriting === doc.id}
                          onClick={e => handleInherit(doc.id, e)}
                        >
                          {inheriting === doc.id
                            ? <i className="fas fa-spinner fa-spin" />
                            : <><i className="fas fa-copy" /> {t('Documents.inherit')}</>}
                        </button>
                        <i className="fas fa-chevron-right" onClick={() => handleView(doc)} style={{ cursor: 'pointer' }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── My Documents tab ── */}
      {tab === 'my-docs' && (
        <div className="doc-card">
          {orgDocsLoading ? (
            <div className="doc-loading"><div className="doc-spinner" /></div>
          ) : orgDocs.length === 0 ? (
            <div className="doc-empty">
              <i className="fas fa-folder-open" style={{ fontSize: 32, marginBottom: 12, color: '#94a3b8' }} />
              <p>{t('Documents.noOrgDocs')}</p>
            </div>
          ) : (
            <div className="doc-grid">
              {orgDocs.map(doc => (
                <div key={doc.id} className="doc-item"
                  onClick={() => handleOpenOrgDoc(doc)}>
                  <div className="doc-item-icon">
                    <i className="fas fa-file-alt" style={{ color: '#1e3a5f' }} />
                  </div>
                  <div className="doc-item-body">
                    <div className="doc-item-title">{doc.title}</div>
                    <div className="doc-item-meta">
                      <WorkflowBadge status={doc.workflowStatus} />
                      {doc.category && <span className="doc-cat-badge">{doc.category}</span>}
                      {doc.fileUrl
                        ? <span style={{ fontSize: 11, color: '#94a3b8' }}><i className="fas fa-paperclip" /> {doc.fileName}</span>
                        : <span style={{ fontSize: 11, color: '#94a3b8' }}>v{doc.currentVersion}</span>}
                    </div>
                    {doc.sourceTemplate && (
                      <p className="doc-item-desc" style={{ fontSize: 11 }}>
                        {t('Documents.inheritedFrom', { title: doc.sourceTemplate.title })}
                      </p>
                    )}
                  </div>
                  <div className="doc-item-arrow" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {doc.workflowStatus === 'IN_FORCE' && (
                      <button title={t('Documents.unpublish', 'Unpublish')} onClick={e => handleUnpublishOrgDoc(doc, e)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b45309', fontSize: 13 }}>
                        <i className="fas fa-rotate-left" />
                      </button>
                    )}
                    {doc.workflowStatus === 'DRAFT' && (
                      <button title={t('Documents.deleteDoc', 'Delete')} onClick={e => handleDeleteOrgDoc(doc, e)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13 }}>
                        <i className="fas fa-trash" />
                      </button>
                    )}
                    <i className="fas fa-chevron-right" style={{ color: '#94a3b8' }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Template preview modal */}
      {previewDoc && (
        <div className="doc-overlay" onClick={() => setPreviewDoc(null)}>
          <div className="doc-modal doc-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="doc-modal-header">
              <div>
                <span className="doc-preview-title">{previewDoc.title}</span>
                <span className="doc-id-badge" style={{ marginLeft: 8 }}>{previewDoc.documentId}</span>
              </div>
              <button className="doc-modal-close" onClick={() => setPreviewDoc(null)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="doc-preview-body">
              {previewLoading ? (
                <div className="doc-loading"><div className="doc-spinner" /></div>
              ) : (
                <div className="doc-branded-preview">
                  <div className="doc-branded-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {orgProfile?.logoUrl
                        ? <img src={orgProfile.logoUrl} alt={t('Documents.logoAlt')} style={{ height: 48, maxWidth: 120, objectFit: 'contain', borderRadius: 6 }} />
                        : <div style={{ width: 48, height: 48, borderRadius: 8, background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fas fa-building" style={{ color: '#94a3b8', fontSize: 20 }} />
                          </div>}
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>{orgProfile?.organizationName || t('Documents.yourOrganization')}</div>
                        {orgProfile?.orgWebsiteUrl && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{orgProfile.orgWebsiteUrl}</div>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#64748b' }}>
                      <div style={{ fontWeight: 600 }}>{previewDoc.documentId}</div>
                      <div>{previewDoc.category}</div>
                    </div>
                  </div>
                  <pre className="doc-preview-text">{previewContent}</pre>
                </div>
              )}
            </div>
            <div className="doc-modal-footer">
              <button className="doc-btn-secondary" onClick={() => setPreviewDoc(null)}>{t('Documents.close')}</button>
              <button
                className="doc-btn-primary"
                disabled={inheriting === previewDoc.id}
                onClick={e => { handleInherit(previewDoc.id, e); setPreviewDoc(null); }}
              >
                {inheriting === previewDoc.id
                  ? <><i className="fas fa-spinner fa-spin" /> {t('Documents.inheriting')}</>
                  : <><i className="fas fa-copy" /> {t('Documents.inheritThisTemplate')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div className="doc-overlay" onClick={() => setShowUpload(false)}>
          <div className="doc-modal" onClick={e => e.stopPropagation()}>
            <div className="doc-modal-header">
              <span>{t('Documents.uploadDocument')}</span>
              <button className="doc-modal-close" onClick={() => setShowUpload(false)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="doc-modal-body">
              <div className="doc-field">
                <label>{t('Documents.titleLabel')}</label>
                <input
                  className="doc-input"
                  placeholder={t('Documents.titlePlaceholder')}
                  value={uploadForm.title}
                  onChange={e => setUploadForm({ ...uploadForm, title: e.target.value })}
                />
              </div>
              <div className="doc-field">
                <label>{t('Documents.categoryLabel')}</label>
                <select
                  className="doc-input"
                  value={uploadForm.category}
                  onChange={e => setUploadForm({ ...uploadForm, category: e.target.value })}
                >
                  <option value="">{t('Documents.selectCategory')}</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="doc-field">
                <label>{t('Documents.descriptionLabel')}</label>
                <textarea
                  className="doc-input doc-textarea"
                  placeholder={t('Documents.descriptionPlaceholder')}
                  rows={3}
                  value={uploadForm.description}
                  onChange={e => setUploadForm({ ...uploadForm, description: e.target.value })}
                />
              </div>
              <div className="doc-field">
                <label>{t('Documents.fileLabel')}</label>
                <div className="doc-file-zone" onClick={() => fileRef.current?.click()}>
                  <input
                    ref={fileRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={e => setUploadFile(e.target.files[0] || null)}
                  />
                  {uploadFile
                    ? <span className="doc-file-name"><i className="fas fa-file" /> {uploadFile.name}</span>
                    : <span className="doc-file-placeholder"><i className="fas fa-cloud-upload-alt" /> {t('Documents.clickToSelectFile')}</span>}
                </div>
              </div>
            </div>
            <div className="doc-modal-footer">
              <button className="doc-btn-secondary" onClick={() => setShowUpload(false)}>{t('Documents.cancel')}</button>
              <button className="doc-btn-primary" onClick={handleUpload} disabled={uploading}>
                {uploading ? t('Documents.uploading') : t('Documents.upload')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documents;
